import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { RotateCw, TriangleAlert } from 'lucide-react';
import { sbg } from '../../theme/sbgTokens';
import { EmptyMark } from './BrandMark';

export interface EmptyStateProps {
  /** What is empty, plainly: "No requests yet", "No employees match these filters". */
  title: React.ReactNode;
  /** What will appear here, or which filter excluded everything. Max ~40ch. */
  body?: React.ReactNode;
  /** The first action (a primary or secondary Button), or "Clear filters". */
  action?: React.ReactNode;
  secondaryAction?: React.ReactNode;
  /** Replace the brand mark with a 24px icon in an icon tile. */
  icon?: React.ReactNode;
  /** Tighter padding for use inside cards and table rows. */
  compact?: boolean;
  sx?: SxProps<Theme>;
}

/**
 * Empty state (book §11.15): the quiet brand mark, a heading-4 title, one
 * secondary line, and the next step. Never cute, never selling.
 */
const EmptyState: React.FC<EmptyStateProps> = ({ title, body, action, secondaryAction, icon, compact = false, sx }) => (
  <Box
    sx={{
      display: 'grid',
      justifyItems: 'center',
      textAlign: 'center',
      gap: 1,
      py: compact ? 3 : 5,
      px: 2,
      ...sx,
    }}
  >
    {icon ? (
      <Box
        aria-hidden="true"
        sx={{
          width: 40,
          height: 40,
          borderRadius: sbg.radius.lg,
          bgcolor: sbg.bg.tile,
          color: sbg.icon.tile,
          display: 'grid',
          placeItems: 'center',
          mb: 1,
          '& > svg': { width: 20, height: 20 },
        }}
      >
        {icon}
      </Box>
    ) : (
      <EmptyMark size={compact ? 32 : 40} sx={{ mb: 1 }} />
    )}
    <Typography variant="h4" component="p" sx={{ color: sbg.text.primary }}>
      {title}
    </Typography>
    {body && (
      <Typography variant="body1" component="p" sx={{ color: sbg.text.secondary, maxWidth: '44ch' }}>
        {body}
      </Typography>
    )}
    {(action || secondaryAction) && (
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center', mt: 1 }}>
        {secondaryAction}
        {action}
      </Box>
    )}
  </Box>
);

export default EmptyState;

export interface ErrorStateProps {
  /** What happened: "Couldn't load time-off requests". */
  title: React.ReactNode;
  /** Why (if known) and what to do now. Never "Oops", never blame. */
  body?: React.ReactNode;
  onRetry?: () => void;
  retryLabel?: string;
  /** Request id or error code an operator can expand. Never raw stack traces. */
  details?: string;
  compact?: boolean;
  sx?: SxProps<Theme>;
}

/** Error state (book §11.15): what happened, why, what to do now, with Retry where it can work. */
export const ErrorState: React.FC<ErrorStateProps> = ({
  title,
  body = 'Something went wrong on our side. Try again in a minute.',
  onRetry,
  retryLabel = 'Try again',
  details,
  compact = false,
  sx,
}) => (
  <Box role="alert" sx={{ display: 'grid', justifyItems: 'center', textAlign: 'center', gap: 1, py: compact ? 3 : 5, px: 2, ...sx }}>
    <Box
      aria-hidden="true"
      sx={{
        width: 40,
        height: 40,
        borderRadius: sbg.radius.lg,
        bgcolor: sbg.status.danger.bg,
        color: sbg.status.danger.icon,
        display: 'grid',
        placeItems: 'center',
        mb: 1,
      }}
    >
      <TriangleAlert size={20} />
    </Box>
    <Typography variant="h4" component="p" sx={{ color: sbg.text.primary }}>
      {title}
    </Typography>
    {body && (
      <Typography variant="body1" component="p" sx={{ color: sbg.text.secondary, maxWidth: '48ch' }}>
        {body}
      </Typography>
    )}
    {details && (
      <Typography variant="mono" component="p" sx={{ color: sbg.text.tertiary, mt: 0.5 }}>
        {details}
      </Typography>
    )}
    {onRetry && (
      <Button variant="outlined" startIcon={<RotateCw />} onClick={onRetry} sx={{ mt: 1 }}>
        {retryLabel}
      </Button>
    )}
  </Box>
);
